/**
 * MetaUpgradeState — read-only resolver for purchased Heritage Tree levels.
 *
 * Heritage Tree spending lives under saveManager.getMeta().metaUpgrades
 * (id -> level). Effects feed into:
 *   - BASE_STATS multipliers (hpMaxBase, atpRegenBase, biomassMult)
 *   - run-start bonuses (startingBiomass, startingMutationSlots)
 *   - mid-run reactions (damageReduction in AbsorptionSystem,
 *     reviveOnce as a player:damaged hook)
 *   - stage-complete bonus (codexDnaBonus)
 *
 * The class is intentionally thin: it doesn't own any state, just reads
 * from the save and the supplied defs. Stages instantiate it once and
 * pass it to whoever needs to query a stat.
 *
 * Per-run flags (reviveUsed) live on the instance and reset on
 * `notifyStageStart`.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { player } from './PlayerState';
import type { MetaUpgradeDef, MetaStat } from '@/data/types';

export class MetaUpgradeState {
  private defs: Record<string, MetaUpgradeDef>;
  private reviveUsedThisRun = false;
  private removers: (() => void)[] = [];

  constructor(defs: Record<string, MetaUpgradeDef>) {
    this.defs = defs;
    // Wire the revive hook here — once installed it lives for the
    // lifetime of the system; per-run state is reset by notifyStageStart.
    this.removers.push(bus.on('player:damaged', () => this.onDamaged()));
  }

  destroy(): void {
    for (const r of this.removers) r();
    this.removers = [];
  }

  notifyStageStart(): void {
    this.reviveUsedThisRun = false;
  }

  /** Total purchased levels of `id`. 0 if not bought. */
  levelOf(id: string): number {
    return saveManager.getMeta().metaUpgrades[id] ?? 0;
  }

  /** Sum of (perLevel * level) across every upgrade whose effect targets `stat`. */
  getStat(stat: MetaStat): number {
    let total = 0;
    for (const def of Object.values(this.defs)) {
      if (def.effect.stat !== stat) continue;
      const lvl = this.levelOf(def.id);
      if (lvl <= 0) continue;
      total += def.effect.perLevel * lvl;
    }
    return total;
  }

  // ---- transactions

  /** Cost of the next level of `id`, or Infinity if maxed. */
  nextCost(id: string): number {
    const def = this.defs[id]; if (!def) return Infinity;
    const lvl = this.levelOf(id);
    return def.costPerLevel[lvl] ?? Infinity;
  }

  /** True if `id` is at max level (no more costs). */
  isMax(id: string): boolean {
    const def = this.defs[id]; if (!def) return true;
    return this.levelOf(id) >= def.costPerLevel.length;
  }

  /** Buy one level of `id` if affordable. Returns true on success.
   *  NOT refundable — once spent, shards are gone. */
  buy(id: string): boolean {
    const def = this.defs[id]; if (!def) return false;
    if (this.isMax(id)) return false;
    const cost = this.nextCost(id);
    const meta = saveManager.getMeta();
    if (meta.shards < cost) return false;
    meta.shards -= cost;
    meta.metaUpgrades[id] = (meta.metaUpgrades[id] ?? 0) + 1;
    saveManager.save();
    bus.emit('meta:shardsChanged', { shards: meta.shards });
    return true;
  }

  // ---- run-time hooks

  /** Called by stages right after creating the player. Adds startingBiomass
   *  bonus to whatever the save already had. */
  applyRunStartBonuses(): void {
    const startBonus = this.getStat('startingBiomass');
    if (startBonus > 0 && saveManager.getRun().biomass === 0) {
      player.addBiomass(startBonus);
    }
  }

  /** Called by stages on init/restart so the slot baseline reflects the
   *  startingMutationSlots upgrade. */
  applyStartingSlots(): void {
    const bonus = Math.floor(this.getStat('startingMutationSlots'));
    if (bonus <= 0) return;
    const run = saveManager.getRun();
    // Only bump if we're at the default. Don't re-add bonuses to a run
    // that already paid for slot upgrades — that would double-count.
    if (run.slotsOwned < 1 + bonus) {
      run.slotsOwned = 1 + bonus;
    }
  }

  // ---- private

  private onDamaged(): void {
    if (player.hp > 0) return;
    if (this.reviveUsedThisRun) return;
    if (this.getStat('reviveOnce') < 1) return;
    this.reviveUsedThisRun = true;
    const restored = Math.round(player.stats.hpMax * 0.25);
    player.healTo(restored);
    bus.emit('fx:flash', { color: 0xfffacd, alpha: 0.5, duration: 0.6 });
    bus.emit('fx:screenShake', { intensity: 6, duration: 0.3 });
    bus.emit('audio:cue', { id: 'pulse', volume: 0.7 });
  }
}
